import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Field, Button, inr } from "../components/UI";
import Typeahead from "../components/Typeahead";
import { Phone, MessageCircle, Plus, Trash2, Check, X, Calendar, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const searchCustomersAsync = async (q) => {
  const url = q ? `/customers?q=${encodeURIComponent(q)}&limit=50` : "/customers?limit=50";
  const r = await api.get(url);
  return r.data;
};

const statusMeta = {
  pending: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
  converted: { cls: "bg-green-100 text-green-800", label: "Converted ✓" },
  no_response: { cls: "bg-red-100 text-red-700", label: "No Response" },
  cancelled: { cls: "bg-slate-100 text-slate-600", label: "Cancelled" },
};

const tierMeta = {
  premium: { cls: "bg-purple-100 text-purple-800", label: "Premium" },
  normal: { cls: "bg-slate-100 text-slate-700", label: "Normal" },
  low: { cls: "bg-red-50 text-red-700", label: "Low" },
};

const blank = { customer_id: "", expected_order_date: "", expected_items: "", notes: "" };
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function FollowUps() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");
  const [filter, setFilter] = useState("today");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter && filter !== "all" ? { urgency: filter } : {};
      const [l, s, c] = await Promise.all([
        api.get("/followups", { params }),
        api.get("/followups/summary"),
        api.get("/customers"),
      ]);
      setList(l.data); setSummary(s.data); setCustomers(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const openNew = (preset = {}) => {
    setForm({ ...blank, expected_order_date: todayIso(), ...preset });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select customer"); return; }
    try {
      await api.post("/followups", form);
      toast.success("Follow-up scheduled");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setStatus = async (f, status) => {
    try {
      await api.patch(`/followups/${f.id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reschedule = async (f) => {
    const newDate = prompt(`Customer ${f.customer_name} ne nayi date di? (YYYY-MM-DD)`, todayIso());
    if (!newDate) return;
    try {
      await api.patch(`/followups/${f.id}`, { expected_order_date: newDate });
      toast.success("Rescheduled");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (f) => {
    if (!window.confirm("Delete follow-up?")) return;
    try { await api.delete(`/followups/${f.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl" data-testid="followups-page">
      <PageHeader
        title="Follow-ups"
        subtitle="Customer ne kaha 'aaj order de denge' — track every promise, never let it slip"
        action={<Button onClick={() => openNew()} data-testid="add-followup-btn"><Plus size={16}/>New Follow-up</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 stagger">
        <div className="card-flat p-4" data-testid="fu-today">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Today</div>
          <div className="font-display font-bold text-2xl mt-1 text-[#C1121F]">{summary.today || 0}</div>
        </div>
        <div className="card-flat p-4" data-testid="fu-overdue">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Overdue</div>
          <div className="font-display font-bold text-2xl mt-1 text-red-600">{summary.overdue || 0}</div>
        </div>
        <div className="card-flat p-4" data-testid="fu-upcoming">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Upcoming</div>
          <div className="font-display font-bold text-2xl mt-1 text-amber-600">{summary.upcoming || 0}</div>
        </div>
        <div className="card-flat p-4" data-testid="fu-total">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Total Pending</div>
          <div className="font-display font-bold text-2xl mt-1">{summary.total_pending || 0}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {["today", "overdue", "upcoming", "pending", "converted", "all"].map((f) => (
          <button key={f} data-testid={`fu-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${filter === f ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : list.length === 0 ? (
        <EmptyState icon={Phone} title="No follow-ups in this view" hint="Schedule a follow-up after a customer commits to a date for their next order."
          action={<Button onClick={() => openNew()}><Plus size={16}/> New Follow-up</Button>} />
      ) : (
        <div className="space-y-2" data-testid="followups-list">
          {list.map((f) => {
            const sMeta = statusMeta[f.status] || statusMeta.pending;
            const tMeta = tierMeta[f.customer_tier] || tierMeta.normal;
            return (
              <div key={f.id} className={`card-flat p-4 ${f.is_overdue ? "border-l-4 border-l-red-500" : f.is_due_today ? "border-l-4 border-l-[#C1121F]" : ""}`} data-testid={`fu-row-${f.id}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-base">{f.customer_name}</h3>
                      <span className={`badge ${tMeta.cls}`}>{tMeta.label}</span>
                      <span className={`badge ${sMeta.cls}`}>{sMeta.label}</span>
                      {f.is_overdue && <span className="badge bg-red-100 text-red-700"><AlertTriangle size={11}/> Overdue</span>}
                      {f.is_due_today && <span className="badge bg-[#FEF2F2] text-[#C1121F]"><Sparkles size={11}/> Today</span>}
                    </div>
                    {f.expected_items && <div className="text-sm text-[#475569] mt-1">📦 {f.expected_items}</div>}
                    {f.notes && <div className="text-xs text-[#94A3B8] mt-1 italic">&ldquo;{f.notes}&rdquo;</div>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#475569]">
                      <span className="flex items-center gap-1 font-mono-num"><Calendar size={12}/> {f.expected_order_date}</span>
                      {f.customer_phone && (
                        <a href={`tel:${f.customer_phone}`} className="flex items-center gap-1 text-[#C1121F] hover:underline">
                          <Phone size={12}/> {f.customer_phone}
                        </a>
                      )}
                      {f.customer_phone && (
                        <a href={`https://wa.me/${f.customer_phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-green-700 hover:underline">
                          <MessageCircle size={12}/> WhatsApp
                        </a>
                      )}
                      <span className="text-[#94A3B8]">by {f.created_by_name}</span>
                    </div>
                  </div>
                  {f.status === "pending" && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button onClick={() => setStatus(f, "converted")} data-testid={`fu-convert-${f.id}`} className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-1">
                        <Check size={11}/> Got Order
                      </button>
                      <button onClick={() => reschedule(f)} data-testid={`fu-reschedule-${f.id}`} className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 flex items-center gap-1">
                        <Calendar size={11}/> Reschedule
                      </button>
                      <button onClick={() => setStatus(f, "no_response")} data-testid={`fu-noresp-${f.id}`} className="px-2 py-1 rounded text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1">
                        <X size={11}/> No Response
                      </button>
                    </div>
                  )}
                  <button onClick={() => remove(f)} data-testid={`fu-del-${f.id}`} className="text-[#94A3B8] hover:text-red-600 p-1 flex-shrink-0"><Trash2 size={14}/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Schedule Follow-up" maxWidth="max-w-xl">
        <form onSubmit={save} className="space-y-4">
          <Field label="Customer" required>
            <Typeahead
              loadOptions={searchCustomersAsync}
              value={form.customer_id}
              selectedLabel={selectedCustomerLabel}
              onChange={(id, opt) => { setForm({...form, customer_id: id}); setSelectedCustomerLabel(opt?.name || ""); }}
              placeholder="Type to search customer…"
              required
              testid="fu-customer"
              renderMeta={(c) => `${c.type || ""} ${c.phone ? "· " + c.phone : ""} ${c.city ? "· " + c.city : ""}`}
            />
          </Field>
          <Field label="Expected Order Date" required>
            <input data-testid="fu-date" required type="date" value={form.expected_order_date} onChange={(e) => setForm({...form, expected_order_date: e.target.value})} className="input-base" />
          </Field>
          <Field label="Expected Items">
            <input data-testid="fu-items" value={form.expected_items} onChange={(e) => setForm({...form, expected_items: e.target.value})} className="input-base" placeholder="e.g. Letter Pad 5000pcs, Hospital File 2000pcs" />
          </Field>
          <Field label="Notes from call">
            <textarea data-testid="fu-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} placeholder="What did the customer say?" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-followup-btn">Schedule</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
